import { Engine, KeyEvent, Keys, Random, Scene, vec } from 'excalibur';
import { DriftingDoodle } from '../actors/drifting-doodle';
import { PaperBackground } from '../actors/paper-background';
import { applyProductGrants, skinProductId } from '../monetization/catalog';
import { MONETIZATION, isMonetizationActive } from '../monetization/config';
import { grantRemoveAds, grantSkin, isAdsRemoved, ownsSkin } from '../monetization/entitlements';
import { trackEvent } from '../monetization/events';
import { getIap } from '../monetization/iap';
import { Theme, SKINS } from '../themes/skins';
import { activeThemeId, refreshSceneTheme, setTheme } from '../themes/theme';
import { sfx } from '../utils/audio';
import { DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { PlacementRect, randomPointInRects } from '../utils/placement';
import { DOODLE_KINDS } from '../utils/sketch';
import { HandLabel } from '../ui/label';
import { SketchButton } from '../ui/sketch-button';
import { SkinCard } from '../ui/skin-card';

const SHOP_DOODLE_ZONES: PlacementRect[] = [
  { left: 16, top: 120, right: 82, bottom: 1240 },
  { left: 638, top: 120, right: 704, bottom: 1240 },
];

export class ShopScene extends Scene {
  private readonly cards: SkinCard[] = [];
  private feedback!: HandLabel;
  private removeAdsButton!: SketchButton;
  private feedbackTimer = 0;
  private busy = false;

  onInitialize(): void {
    this.add(new PaperBackground());
    this.add(
      new HandLabel({
        name: 'shop-header',
        text: 'shop',
        pos: vec(DESIGN_WIDTH / 2, 92),
        size: 34,
        tone: 'inkSoft',
      }),
    );
    this.add(
      new HandLabel({
        name: 'shop-heading',
        text: 'dress up the clock',
        pos: vec(DESIGN_WIDTH / 2, 160),
        size: 56,
        tone: 'ink',
      }),
    );

    const positions = [vec(200, 372), vec(520, 372), vec(200, 688), vec(520, 688)];
    SKINS.forEach((theme, index) => {
      const card = new SkinCard({
        name: `skin-card-${theme.id}`,
        theme,
        size: 272,
        onPress: () => {
          void this.handleCardPress(theme);
        },
      });
      card.pos = positions[index % positions.length];
      this.cards.push(card);
      this.add(card);
    });

    this.removeAdsButton = new SketchButton({
      name: 'shop-remove-ads',
      text: 'remove ads',
      width: 340,
      height: 64,
      accentTone: 'pink',
      fontSize: 32,
      onPress: this.buyRemoveAds,
    });
    this.removeAdsButton.pos = vec(DESIGN_WIDTH / 2, 916);
    this.add(this.removeAdsButton);

    const restore = new SketchButton({
      name: 'shop-restore',
      text: 'restore',
      width: 220,
      height: 56,
      accentTone: 'blue',
      fontSize: 28,
      onPress: this.restore,
    });
    restore.pos = vec(200, 1024);
    this.add(restore);

    const back = new SketchButton({
      name: 'shop-back',
      text: 'back',
      width: 220,
      height: 56,
      accentTone: 'yellow',
      fontSize: 28,
      onPress: this.goBack,
    });
    back.pos = vec(520, 1024);
    this.add(back);

    this.feedback = new HandLabel({
      name: 'shop-feedback',
      text: '',
      pos: vec(DESIGN_WIDTH / 2, 1108),
      size: 26,
      tone: 'mintDeep',
    });
    this.add(this.feedback);

    this.add(
      new HandLabel({
        name: 'shop-hint',
        text: 'skins repaint the paper, the clock and the doodles',
        pos: vec(DESIGN_WIDTH / 2, 1176),
        size: 22,
        tone: 'inkSoft',
        maxWidth: 620,
      }),
    );

    const rng = new Random(SEEDS.drifting + 99);
    for (let i = 0; i < 6; i++) {
      this.add(
        new DriftingDoodle({
          name: `shop-doodle-${i}`,
          kind: DOODLE_KINDS[(i + 2) % DOODLE_KINDS.length],
          pos: randomPointInRects(rng, SHOP_DOODLE_ZONES),
          size: rng.floating(12, 18),
          toneIndex: i,
          seed: SEEDS.drifting + 300 + i * 11,
        }),
      );
    }

    this.refreshCards();
  }

  onActivate(): void {
    refreshSceneTheme(this);
    this.refreshCards();
    this.feedback.text = '';
    this.feedbackTimer = 0;
    this.busy = false;
    this.input.keyboard.on('press', this.handleKey);
    trackEvent('shop_opened');
  }

  onDeactivate(): void {
    this.input.keyboard.off('press', this.handleKey);
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    if (this.feedbackTimer > 0) {
      this.feedbackTimer = Math.max(0, this.feedbackTimer - dt);
      if (this.feedbackTimer === 0) {
        this.feedback.text = '';
      }
    }
  }

  private refreshCards(): void {
    for (const card of this.cards) {
      card.setState(ownsSkin(card.themeId), activeThemeId() === card.themeId);
    }
    this.removeAdsButton.setVisible(
      isMonetizationActive() && MONETIZATION.iap.removeAds && !isAdsRemoved(),
    );
  }

  private async handleCardPress(theme: Theme): Promise<void> {
    if (this.busy) {
      return;
    }
    sfx.resume();
    if (ownsSkin(theme.id)) {
      setTheme(theme.id);
      trackEvent('skin_selected', { skin: theme.id });
      this.refreshCards();
      this.showFeedback(`${theme.name} it is!`);
      sfx.doodle();
      return;
    }
    if (!isMonetizationActive() || !MONETIZATION.iap.cosmetics) {
      this.showFeedback('not for sale yet');
      return;
    }
    this.busy = true;
    this.showFeedback('asking the store...');
    const outcome = await getIap().purchase(skinProductId(theme.id));
    this.busy = false;
    if (outcome === 'purchased') {
      grantSkin(theme.id);
      setTheme(theme.id);
      this.refreshCards();
      this.showFeedback(`${theme.name} unlocked!`);
      sfx.doubleHit();
      return;
    }
    this.showFeedback(outcome === 'cancelled' ? 'no worries!' : 'store unavailable');
  }

  private buyRemoveAds = (): void => {
    void this.runRemoveAds();
  };

  private async runRemoveAds(): Promise<void> {
    if (this.busy || !isMonetizationActive() || !MONETIZATION.iap.removeAds || isAdsRemoved()) {
      return;
    }
    this.busy = true;
    sfx.resume();
    this.showFeedback('asking the store...');
    const outcome = await getIap().purchase(MONETIZATION.ids.removeAdsProduct);
    this.busy = false;
    if (outcome === 'purchased') {
      grantRemoveAds();
      this.refreshCards();
      this.showFeedback('ads gone — thank you!');
      sfx.doodle();
      return;
    }
    this.showFeedback(outcome === 'cancelled' ? 'no worries!' : 'store unavailable');
  }

  private restore = (): void => {
    void this.runRestore();
  };

  private async runRestore(): Promise<void> {
    if (this.busy) {
      return;
    }
    this.busy = true;
    sfx.resume();
    this.showFeedback('checking...');
    const restored = await getIap().restore();
    this.busy = false;
    applyProductGrants(restored);
    this.refreshCards();
    this.showFeedback(
      restored.length > 0
        ? `restored ${restored.length} purchase${restored.length === 1 ? '' : 's'}`
        : 'nothing to restore',
    );
  }

  private showFeedback(text: string): void {
    this.feedback.text = text;
    this.feedbackTimer = 3.5;
  }

  private goBack = (): void => {
    sfx.resume();
    sfx.start();
    void this.engine.goToScene('title');
  };

  private handleKey = (evt: KeyEvent): void => {
    if (evt.key === Keys.Escape || evt.key === Keys.Backspace) {
      this.goBack();
    }
  };
}
